import { clientCreateTicket, apiConfigured } from '../api.js'
import {
  applyIssueToSubject,
  composeSubject,
  issueChipsHtml,
  SLA_HINT,
} from '../issueTypes.js'
import {
  clearFieldErrors,
  escapeHtml,
  missingFieldsMessage,
  validateRequiredFields,
} from '../ui.js'
import {
  clearClientSession,
  getClientSession,
  hasPermission,
  isClientLoggedIn,
} from '../clientAuth.js'

const CREATE_PREFILL_KEY = 'portal_create_prefill'

export function renderClientCreate() {
  return `
    <div class="portal-card create-sheet">
      <div class="create-sheet-head">
        <div>
          <p class="portal-eyebrow">Nueva incidencia</p>
          <h1>Cuéntanos qué ocurre</h1>
          <p class="portal-muted" id="client-create-context">Empresa e instalación salen de tu sesión.</p>
        </div>
        <a href="/mi-instalacion" data-link class="portal-btn">Volver al portal</a>
      </div>
      <form id="client-create-form" class="create-form create-form--auth" novalidate>
        <input type="hidden" name="installation_id" id="installation_id" value="" />
        <div class="portal-field">
          <label class="portal-label" for="client-create-company">Empresa</label>
          <input class="portal-input" id="client-create-company" type="text" readonly tabindex="-1" />
        </div>
        <div class="portal-field">
          <span class="portal-label">Tipo de incidencia</span>
          ${issueChipsHtml()}
        </div>
        <div class="portal-field">
          <label class="portal-label" for="subject">Asunto *</label>
          <input class="portal-input" id="subject" name="subject" required maxlength="255" autocomplete="off" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="description">Descripción *</label>
          <textarea class="portal-textarea" id="description" name="description" required maxlength="10000" rows="6"
            placeholder="Ej. El WiFi de la piscina no da cobertura desde las 18:00"></textarea>
        </div>
        <p class="create-sla">${SLA_HINT}</p>
        <div class="create-sticky">
          <button type="submit" class="portal-btn portal-btn-primary portal-btn-block">Enviar incidencia</button>
          <a href="/ia" data-link class="create-alt">o usar asistente IA</a>
        </div>
      </form>
      <p id="client-create-msg" class="portal-muted portal-form-msg" role="status"></p>
      <p id="client-create-success" class="portal-form-msg" hidden></p>
    </div>
  `
}

export function bindClientCreate(root, { navigate }) {
  if (!isClientLoggedIn()) {
    navigate('/cliente', { replace: true })
    return
  }

  if (!hasPermission('client_portal.tickets.write') && !getClientSession().staff?.is_superadmin) {
    const denied = root.querySelector('#client-create-msg')
    if (denied) {
      denied.textContent = 'No tienes permiso para crear tickets.'
      denied.classList.add('is-error')
    }
    return
  }

  const session = getClientSession()
  const form = root.querySelector('#client-create-form')
  const msg = root.querySelector('#client-create-msg')
  const success = root.querySelector('#client-create-success')
  const btn = form?.querySelector('button[type="submit"]')
  const context = root.querySelector('#client-create-context')
  const companyEl = root.querySelector('#client-create-company')
  const subjectEl = form?.querySelector('#subject')

  let installationId = session.installations?.[0]?.id || null
  try {
    const pref = sessionStorage.getItem('portal_preferred_installation_id')
    if (pref) {
      const prefId = Number(pref)
      if (prefId && (session.installations || []).some((i) => Number(i.id) === prefId)) {
        installationId = prefId
      }
    }
  } catch (_) {}

  const inst = (session.installations || []).find((i) => Number(i.id) === Number(installationId))
  const orgName =
    session.client?.organization?.name ||
    inst?.organization_name ||
    session.organizations?.[0]?.name ||
    ''
  const who = session.client?.email || session.staff?.email || ''
  if (context) {
    context.textContent = [
      orgName ? `Org: ${orgName}` : null,
      inst?.name ? `Instalación: ${inst.name}` : null,
      who ? `Autor: ${who}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Describe el problema. Datos de sesión automáticos.'
  }
  if (companyEl) {
    companyEl.value = [orgName, inst?.name].filter(Boolean).join(' · ') || 'Sesión cliente'
  }
  if (form?.installation_id && installationId) {
    form.installation_id.value = String(installationId)
  }

  try {
    const raw = sessionStorage.getItem(CREATE_PREFILL_KEY)
    if (raw && form) {
      const prefill = JSON.parse(raw)
      sessionStorage.removeItem(CREATE_PREFILL_KEY)
      if (prefill.description) form.description.value = prefill.description
      if (prefill.subject) form.subject.value = prefill.subject
    }
  } catch (_) {}

  applyIssueToSubject(form, subjectEl)
  form?.querySelectorAll('input[name="issue_type"]').forEach((el) => {
    el.addEventListener('change', () => applyIssueToSubject(form, subjectEl))
  })

  form?.querySelectorAll('input, textarea').forEach((el) => {
    el.addEventListener('input', () => {
      clearFieldErrors(el.closest('.portal-field') || form)
      msg?.classList.remove('is-error')
    })
  })

  form?.addEventListener('submit', async (ev) => {
    ev.preventDefault()
    msg.classList.remove('is-error')
    success.hidden = true
    success.innerHTML = ''

    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible.'
      msg.classList.add('is-error')
      return
    }

    if (subjectEl) {
      subjectEl.value = composeSubject(form, subjectEl, form.description)
    }

    const { ok, missing } = validateRequiredFields(form, [
      { name: 'subject', label: 'Asunto' },
      { name: 'description', label: 'Descripción' },
    ])
    if (!ok) {
      msg.textContent = missingFieldsMessage(missing)
      msg.classList.add('is-error')
      return
    }

    const payload = {
      subject: String(form.subject.value || '').trim(),
      description: String(form.description.value || '').trim(),
    }
    const iid = Number(form.installation_id?.value || installationId || 0)
    if (iid) payload.installation_id = iid

    btn && (btn.disabled = true)
    msg.textContent = 'Enviando…'
    try {
      const data = await clientCreateTicket(payload)
      const path = data.ticket?.portal_path || `/t/${data.ticket?.access_token}`
      const number = data.ticket?.ticket_number || ''
      msg.textContent = ''
      success.hidden = false
      success.innerHTML = `${escapeHtml(data.message || 'Ticket creado.')}
        ${number ? ` <strong>${escapeHtml(number)}</strong>` : ''}
        — <a href="${escapeHtml(path)}" data-link>Ver ticket</a>`
      form.reset()
      if (form.installation_id && installationId) {
        form.installation_id.value = String(installationId)
      }
      if (companyEl) {
        companyEl.value = [orgName, inst?.name].filter(Boolean).join(' · ') || 'Sesión cliente'
      }
      applyIssueToSubject(form, subjectEl)
      btn && (btn.disabled = false)
      setTimeout(() => navigate(path), 900)
    } catch (e) {
      if (e.status === 401) {
        clearClientSession()
        navigate('/cliente', { replace: true })
        return
      }
      msg.textContent = e.message || 'Error al crear el ticket'
      msg.classList.add('is-error')
      btn && (btn.disabled = false)
    }
  })
}
